import { CheckCircle, Copy, ExternalLink } from 'lucide-react'
import { toast } from 'react-toastify'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  nativeCsvWriter,
  type NativeCsvWriterResult,
} from 'sprites-types/workflow/tools/nativeCsvWriter'
import type { ContextAction } from '../shared/ContextMenuWrapper'
import { FileResult } from '../shared/FileResult'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function NativeCsvWriterDisplayComponent({
  nodeExecution,
  onOpenPanel,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(nativeCsvWriter, nodeExecution.inputs)
  console.log('nodeExecution', nodeExecution)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const parsed = parseResult.data

  // Handle emitMode: 'multi' case (array of results)
  if (Array.isArray(parsed)) {
    return (
      <div className="space-y-2">
        {parsed.map((result, index) => (
          <SingleCsvWriterResult
            key={index}
            result={result}
            inputs={nodeExecution.inputs}
            onOpenPanel={onOpenPanel}
          />
        ))}
      </div>
    )
  }

  // Handle emitMode: 'single' case (single result)
  return (
    <SingleCsvWriterResult
      result={parsed}
      inputs={nodeExecution.inputs}
      onOpenPanel={onOpenPanel}
    />
  )
}

interface SingleCsvWriterResultProps {
  result: NativeCsvWriterResult
  inputs: NodeExecution['inputs']
  onOpenPanel?: (content: unknown, breadcrumbs: unknown[]) => void
}

function SingleCsvWriterResult({ result, inputs, onOpenPanel }: SingleCsvWriterResultProps) {
  // Calculate row and column counts from input data
  const inputData = Array.isArray(inputs.results) ? inputs.results : []
  const rowCount = inputData.length
  const columnCount =
    inputData.length > 0 && Array.isArray(inputData[0]?.hs) ? inputData[0].hs.length : 0

  const subtitle = [
    rowCount > 0 ? `${rowCount.toLocaleString()} rows` : '',
    columnCount > 0 ? `${columnCount} columns` : '',
  ]
    .filter(Boolean)
    .join(' • ')

  // Type guard and discriminated union handling
  if (result.type === 'text') {
    // Raw CSV content - create blob URL for download and preview
    const blob = new Blob([result.content], { type: 'text/csv' })
    const blobUrl = URL.createObjectURL(blob)

    return (
      <FileResult
        file={{
          name: 'export.csv',
          url: blobUrl,
          downloadUrl: blobUrl,
          mimeType: 'text/csv',
        }}
        customIcon={
          <div className="rounded-full bg-green-100 p-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
        }
        customLabel="CSV file generated"
        customSubtitle={subtitle || 'Raw CSV content'}
        onOpenPanel={onOpenPanel}
        showPreview={true}
      />
    )
  }

  // File type (URL-based or Google Sheets)
  const fileUrl = result.source.value
  const isGoogleSheets = fileUrl.includes('docs.google.com/spreadsheets')

  const contextActions: ContextAction[] = [
    {
      id: 'copy-url',
      label: 'Copy URL',
      icon: <Copy className="h-3 w-3" />,
      onClick: () => {
        navigator.clipboard.writeText(fileUrl)
        toast.success('URL copied to clipboard')
      },
    },
  ]

  if (isGoogleSheets) {
    contextActions.push({
      id: 'open-sheet',
      label: 'Open in new tab',
      icon: <ExternalLink className="h-3 w-3" />,
      onClick: () => {
        window.open(fileUrl, '_blank')
      },
    })
  }

  return (
    <FileResult
      file={{
        name: result.filename,
        url: fileUrl,
        downloadUrl: fileUrl,
        mimeType: isGoogleSheets ? 'application/vnd.google-apps.spreadsheet' : 'text/csv',
      }}
      customIcon={
        <div className="rounded-full bg-green-100 p-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
        </div>
      }
      customLabel={isGoogleSheets ? 'Google Sheet updated' : 'CSV file generated'}
      customSubtitle={subtitle}
      contextActions={contextActions}
      onOpenPanel={onOpenPanel}
      showPreview={!isGoogleSheets}
    />
  )
}

function downloadNativeCsvWriter(nodeExecution: NodeExecution): Promise<void> {
  const schema = getToolResultSchema(nativeCsvWriter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    console.error('Schema validation failed for download:', parseResult.error)
    return Promise.resolve()
  }

  const parsed = parseResult.data

  // Handle emitMode: 'multi' case - download first result or all as zip
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return Promise.resolve()
    // For simplicity, download the first result
    // Could be enhanced to download all as a zip file
    return downloadSingleCsvWriter(parsed[0])
  }

  // Handle emitMode: 'single' case
  return downloadSingleCsvWriter(parsed)
}

function downloadSingleCsvWriter(result: NativeCsvWriterResult): Promise<void> {
  if (result.type === 'text') {
    // Download raw CSV content as a file
    const blob = new Blob([result.content], { type: 'text/csv' })
    const blobUrl = window.URL.createObjectURL(blob)
    const downloadLink = document.createElement('a')
    downloadLink.href = blobUrl
    downloadLink.download = 'export.csv'
    downloadLink.click()
    window.URL.revokeObjectURL(blobUrl)
    return Promise.resolve()
  }

  // File type
  const fileUrl = result.source.value
  const isGoogleSheets = fileUrl.includes('docs.google.com/spreadsheets')

  if (isGoogleSheets) {
    // Open Google Sheets URL
    window.open(fileUrl, '_blank')
    return Promise.resolve()
  }

  // Download from URL
  return fetch(fileUrl)
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok')
      return response.blob()
    })
    .then((blob) => {
      const blobUrl = window.URL.createObjectURL(blob)
      const downloadLink = document.createElement('a')
      downloadLink.href = blobUrl
      downloadLink.download = result.filename
      downloadLink.click()
      window.URL.revokeObjectURL(blobUrl)
    })
    .catch((error) => {
      console.error('Download failed:', error)
      toast.error('Download failed')
    })
}

function nativeCsvWriterToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(nativeCsvWriter, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const parsed = parseResult.data

  // Handle emitMode: 'multi' case
  if (Array.isArray(parsed)) {
    return parsed
      .map((result, index) => {
        const header = parsed.length > 1 ? `### CSV File ${index + 1}\n\n` : ''
        return header + singleCsvWriterToMarkdown(result, nodeExecution.inputs)
      })
      .join('\n')
  }

  // Handle emitMode: 'single' case
  return singleCsvWriterToMarkdown(parsed, nodeExecution.inputs)
}

function singleCsvWriterToMarkdown(
  result: NativeCsvWriterResult,
  inputs: NodeExecution['inputs']
): string {
  // Calculate row and column counts from input data
  const inputData = Array.isArray(inputs.results) ? inputs.results : []
  const rowCount = inputData.length
  const columnCount =
    inputData.length > 0 && Array.isArray(inputData[0]?.hs) ? inputData[0].hs.length : 0

  if (result.type === 'text') {
    return `## CSV File Generated

**Type:** Raw CSV content
${rowCount > 0 ? `**Rows:** ${rowCount.toLocaleString()}` : ''}
${columnCount > 0 ? `**Columns:** ${columnCount}` : ''}

---
`
  }

  // File type
  const fileUrl = result.source.value
  const isGoogleSheets = fileUrl.includes('docs.google.com/spreadsheets')

  if (isGoogleSheets) {
    return `## Google Sheet Updated

${rowCount > 0 ? `**Rows:** ${rowCount.toLocaleString()}` : ''}
${columnCount > 0 ? `**Columns:** ${columnCount}` : ''}
**URL:** ${fileUrl}

---
`
  }

  return `## CSV File Generated

**Filename:** ${result.filename}
${rowCount > 0 ? `**Rows:** ${rowCount.toLocaleString()}` : ''}
${columnCount > 0 ? `**Columns:** ${columnCount}` : ''}
**URL:** ${fileUrl}

---
`
}

export const NativeCsvWriterDisplay = createWorkflowToolComponent(
  NativeCsvWriterDisplayComponent,
  downloadNativeCsvWriter,
  nativeCsvWriterToMarkdown
)
